import {
  BrowserRouter,
  Routes,
  Route,
  Link
} from 'react-router-dom';

import {
  Alignment,
  Button,
  Menu,
  MenuItem,
  Navbar,
  NavbarDivider,
  NavbarGroup,
  NavbarHeading,
  Popover,
  Classes,
} from "@blueprintjs/core";

import MarketClock from './components/MarketClock';
import GenerationChartContainer from './components/GenerationChartContainer';
import SystemLoadChartContainer from './components/SystemLoadChartContainer';
import GenerationOffersContainer from './components/GenerationOffersContainer';
import OfferStackContainer from './components/OfferStackContainer';
import WindForecastChartContainer from './components/WindForecastChartContainer';
import AsxPortfolioMarginContainer from './components/AsxPortfolioMarginContainer';
import WindDemandBalanceContainer from './components/WindDemandBalanceContainer';
import NdsiChartContainer from './components/NdsiChartContainer';
import ChangedOutagesContainer from "./components/ChangedOutagesContainer";
import GenerationOutagesContainer from './components/GenerationOutagesContainer';

import { Amplify, Auth } from 'aws-amplify';
import { withAuthenticator } from '@aws-amplify/ui-react';

import '@aws-amplify/ui-react/styles.css';
import MMReviewContainer from "./components/MMReviewContainer";

const DISABLE_AUTH = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DISABLE_AUTH === 'true';

Amplify.configure({
  ...(DISABLE_AUTH
  ? {} // no Auth section when disabled
  : {Auth: {
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    mandatorySignIn: true,
  }
}),
  API: {
    endpoints: [
      {
        name: 'SparcAPI',
        endpoint: process.env.REACT_APP_SPARC_API_ENDPOINT,
        custom_header: async () => {
          if (DISABLE_AUTH) return {}; // no Authorization header locally
          const jwt = (await Auth.currentSession()).getAccessToken().getJwtToken();
          return { Authorization: `Bearer ${jwt}` };
        },
      }
    ]
  }
});


const COGNITO_RESTRICTED_GROUP = 'restricted';


function App({signOut, user}) {
  return (
    <BrowserRouter>
      <div>
        <Home signOut={signOut} user={user}/>
        <Routes>
          <Route path="/generation-charts" element={<GenerationChartContainer apiUrl="/ems/generation/chart_grouping"/>} />
          <Route path="/10min-generation-charts" element={<GenerationChartContainer apiUrl="/ems/10min_generation/chart_grouping"/>} />
          <Route path="/load-charts" element={<SystemLoadChartContainer />} />
          <Route path="/generation-offers" element={<GenerationOffersContainer />} />
          <Route path="/offer-stacks" element={<OfferStackContainer />} />
          <Route path="/wind-forecast-charts" element={<WindForecastChartContainer />} />
          <Route path="/asx-portfolio-margin" element={<AsxPortfolioMarginContainer />} />
          <Route path="/wind-demand-balance" element={<WindDemandBalanceContainer />} />
          <Route path="/ndsi-charts" element={<NdsiChartContainer />} />
          <Route path="/session-review" element={<MMReviewContainer />} />
          <Route path="/outages-generation-capacity" element={<GenerationOutagesContainer />} />
          <Route path="/outages-changes" element={<ChangedOutagesContainer />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}


function Home({ signOut, user }) {
  let groups = user.signInUserSession.accessToken.payload["cognito:groups"];
  if (groups === undefined) {
    groups = [];
  }

  const outagesMenu = (
        <Menu>
          <MenuItem icon="comparison" text="Recent changes" href="/outages-changes"/>
          <MenuItem icon="stacked-chart" text="Generation capacity" href="/outages-generation-capacity" />
        </Menu>
      );

  // for icons see: https://blueprintjs.com/docs/#icons/icons-list
  return (
    <div className="App">
      <Navbar>

        <NavbarGroup align={Alignment.LEFT}>
          <NavbarHeading>Sparc</NavbarHeading>
          <NavbarDivider />
        </NavbarGroup>

        <NavbarGroup align={Alignment.LEFT}>

          <Link to="/generation-charts">
            <Button
              className={Classes.MINIMAL}
              icon="rig"
              text="Generation"
            />
          </Link>

          <Link to="/10min-generation-charts">
            <Button
              className={Classes.MINIMAL}
              icon="rig"
              text="10min Generation"
            />
          </Link>

          <Link to="/generation-offers">
            <Button
              className={Classes.MINIMAL}
              icon="full-stacked-chart"
              text="Historic Offers"
            />
          </Link>

          <Link to="/offer-stacks">
            <Button
              className={Classes.MINIMAL}
              icon="full-stacked-chart"
              text="Offer Stacks"
            />
          </Link>

          <Link to="/load-charts">
            <Button
              className={Classes.MINIMAL}
              icon="timeline-line-chart"
              text="Demand"
            />
          </Link>


          <Link to="/wind-forecast-charts">
            <Button
              className={Classes.MINIMAL}
              icon="timeline-line-chart"
              text="Wind Forecasts"
            />
          </Link>

          <Link to="/wind-demand-balance">
            <Button
              className={Classes.MINIMAL}
              icon="timeline-line-chart"
              text="DNoW"
            />
          </Link>

          <Link to="/ndsi-charts">
            <Button
              className={Classes.MINIMAL}
              icon="timeline-line-chart"
              text="NDSI"
            />
          </Link>

          <Link to="/session-review">
            <Button
              className={Classes.MINIMAL}
              icon="timeline-line-chart"
              text="MM Session"
            />
          </Link>

          <Link to="/asx-portfolio-margin">
            <Button
              className={Classes.MINIMAL}
              icon="grid"
              text="Risk"
            />
          </Link>

          <span style={{ marginBottom: '5px' }}>
           <Popover content={outagesMenu} placement="bottom-start">
              <Button className={Classes.MINIMAL} icon="lightning" text="Outages" />
            </Popover>
          </span>

        </NavbarGroup>

        <NavbarGroup align={Alignment.RIGHT}>
          <Button onClick={signOut}>Sign out</Button>
        </NavbarGroup>

        <NavbarGroup align={Alignment.RIGHT}>
          <MarketClock />
        </NavbarGroup>

      </Navbar>
    </div>
  );
}

const fakeUser = {
  username: 'dev-user',
  signInUserSession: { accessToken: { payload: { 'cognito:groups': [] } } },
};

function NoAuthApp() {
  return <App user={fakeUser} signOut={() => {}} />;
}

// Alternatively, one-liner is fine too:
export default (DISABLE_AUTH ? NoAuthApp : withAuthenticator(App));